import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getMediaUrl,
  resolveBackendRelativeUrlsInHtml,
} from "../../../utils/api";
import { normalizeAdminRichText } from "../../../utils/richText";
import {
  EXTERNAL_VIDEO_IFRAME_PROPS,
  toEmbedUrl,
} from "../../../utils/videoEmbed";
import { saveAs } from "file-saver";
import "./MediaCarousel.css";

function resolveVideoMeta(item) {
  const stripVideoFileLinks = (html) => {
    if (!html || typeof html !== "string") return html;
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll("a[href]").forEach((a) => {
        const href = a.getAttribute("href") || "";
        const pathOnly = href.split("?")[0].split("#")[0];
        if (!/\.(mp4|webm|ogg|ogv|mp3)$/i.test(pathOnly)) return;
        a.remove();
      });
      return doc.body.innerHTML;
    } catch {
      return html;
    }
  };

  const href = item?.url ?? item?.link ?? item?.enlace ?? "";
  const contenido = String(item?.contenido ?? "");
  const matchEmbed = contenido.match(/url="([^"]+)"/i);
  const rawUrl = href || (matchEmbed ? matchEmbed[1] : "");
  const embedSrc = toEmbedUrl(rawUrl);
  const looksFile = rawUrl && /\.(mp4|webm|ogg|ogv|mp3)$/i.test(rawUrl);
  const fileSrc = looksFile ? toEmbedUrl(rawUrl) : "";
  const contenidoRaw = String(item?.contenido ?? "").trim();
  const contenidoHtml = contenidoRaw
    ? stripVideoFileLinks(
        resolveBackendRelativeUrlsInHtml(
          normalizeAdminRichText(item?.contenido),
        ),
      )
    : "";

  let mime = "video/mp4";
  let isAudio = false;
  if (looksFile) {
    const extMatch = String(fileSrc)
      .split("?")[0]
      .match(/\.([a-z0-9]+)$/i);
    const ext = extMatch ? extMatch[1].toLowerCase() : "mp4";
    mime =
      ext === "webm"
        ? "video/webm"
        : ext === "ogg" || ext === "ogv"
          ? "video/ogg"
          : ext === "mp3"
            ? "audio/mpeg"
            : "video/mp4";
    isAudio = ext === "mp3";
  }

  return {
    embedSrc,
    looksFile,
    fileSrc,
    mime,
    contenidoHtml,
    downloadUrl: looksFile ? fileSrc : "",
    hasMedia: Boolean(looksFile && fileSrc) || Boolean(embedSrc),
    isAudio,
  };
}

export default function MediaCarousel({ items, kind, emptyLabel }) {
  const list = Array.isArray(items) ? items : [];
  const [indexActual, setIndexActual] = useState(0);
  const DEFAULT_ZOOM = 1;
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const isPanningRef = useRef(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const zoomExpanded = zoom > DEFAULT_ZOOM;

  const actual = list[indexActual];
  const titulo = actual?.titulo ?? actual?.title ?? "";
  const referencia =
    actual?.referencia_bibliografica ??
    actual?.referencia ??
    actual?.cita ??
    "";

  const src =
    actual?.url ??
    actual?.archivo ??
    actual?.imagen ??
    actual?.path ??
    actual?.src ??
    "";

  const mediaUrl = useMemo(() => {
    if (!src) return "";
    if (typeof src !== "string") return "";
    if (src.startsWith("http://") || src.startsWith("https://")) return src;
    if (src.startsWith("/")) return getMediaUrl(src);
    return getMediaUrl(src);
  }, [src]);

  useEffect(() => {
    setIndexActual((i) => {
      if (list.length === 0) return 0;
      return Math.min(i, list.length - 1);
    });
  }, [list.length]);

  useEffect(() => {
    setZoom(DEFAULT_ZOOM);
    setPan({ x: 0, y: 0 });
  }, [indexActual]);

  useEffect(() => {
    if (zoom <= DEFAULT_ZOOM) setPan({ x: 0, y: 0 });
  }, [zoom]);

  useEffect(() => {
    if (!zoomExpanded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(DEFAULT_ZOOM);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [zoomExpanded]);

  const isVideo = kind === "video";
  const canZoomDownload = !isVideo && Boolean(mediaUrl);
  const videoMeta = useMemo(
    () => (isVideo && actual ? resolveVideoMeta(actual) : null),
    [isVideo, actual],
  );

  if (!list || list.length === 0) {
    return <div className="evt-empty">{emptyLabel}</div>;
  }

  const siguiente = () => setIndexActual((prev) => (prev + 1) % list.length);
  const anterior = () =>
    setIndexActual((prev) => (prev === 0 ? list.length - 1 : prev - 1));

  const buildDownloadName = (url, fallbackKind) => {
    const safeBase =
      String(titulo || `${fallbackKind || kind || "media"}_${indexActual + 1}`)
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .slice(0, 120) || "archivo";
    const urlNoQuery = String(url).split("?")[0].split("#")[0];
    const extMatch = urlNoQuery.match(/\.([a-z0-9]+)$/i);
    const ext = extMatch ? extMatch[1].toLowerCase() : "png";
    return `${safeBase}.${ext}`;
  };

  const downloadFromUrl = async (url, fallbackKind) => {
    if (!url || downloadBusy) return;
    try {
      setDownloadBusy(true);
      const resp = await fetch(url);
      if (!resp.ok) throw new Error("download_failed");
      const blob = await resp.blob();
      saveAs(blob, buildDownloadName(url, fallbackKind));
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadBusy(false);
    }
  };

  const downloadCurrent = () => downloadFromUrl(mediaUrl, kind);
  const downloadVideo = () => downloadFromUrl(videoMeta?.downloadUrl, "video");

  const handlePanStart = (e) => {
    if (!zoomExpanded) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    isPanningRef.current = true;
    setIsPanning(true);
  };

  const handlePanMove = (e) => {
    if (!isPanningRef.current) return;
    e.preventDefault();
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setPan({
      x: panStartRef.current.panX + dx,
      y: panStartRef.current.panY + dy,
    });
  };

  const handlePanEnd = (e) => {
    if (!isPanningRef.current) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    isPanningRef.current = false;
    setIsPanning(false);
  };

  const renderMedia = () => {
    if (isVideo) {
      if (!videoMeta?.hasMedia) {
        return <div className="evt-media-placeholder">Sin video</div>;
      }
      if (videoMeta.looksFile) {
        if (videoMeta.isAudio) {
          return (
            <audio
              key={videoMeta.fileSrc}
              className="evt-media-audio"
              controls
              preload="metadata"
            >
              <source src={videoMeta.fileSrc} type={videoMeta.mime} />
            </audio>
          );
        }
        return (
          <video
            key={videoMeta.fileSrc}
            className="evt-media-video"
            controls
            playsInline
            preload="metadata"
          >
            <source src={videoMeta.fileSrc} type={videoMeta.mime} />
          </video>
        );
      }
      return (
        <iframe
          {...EXTERNAL_VIDEO_IFRAME_PROPS}
          className="evt-media-iframe"
          title={titulo || "Video"}
          src={videoMeta.embedSrc}
        />
      );
    }

    if (!mediaUrl)
      return <div className="evt-media-placeholder">Sin imagen</div>;
    const shrinkStyle =
      zoom < DEFAULT_ZOOM
        ? {
            transform: `scale(${zoom})`,
            transformOrigin: "center center",
          }
        : undefined;
    return (
      <div className="evt-media-viewport" aria-label="Vista de imagen">
        <div className="evt-media-canvas">
          <img
            className="evt-media-img evt-media-img--zoomable"
            src={mediaUrl}
            alt={titulo || "Multimedia"}
            loading="lazy"
            style={shrinkStyle}
          />
        </div>
      </div>
    );
  };

  const renderZoomLayer = () => {
    if (!canZoomDownload || !zoomExpanded || !mediaUrl) return null;
    return (
      <div
        className="evt-media-zoom-layer"
        role="dialog"
        aria-modal="true"
        aria-label="Imagen ampliada"
      >
        <button
          type="button"
          className="evt-media-zoom-layer-close"
          onClick={() => setZoom(DEFAULT_ZOOM)}
          aria-label="Cerrar vista ampliada"
        >
          ×
        </button>
        <div
          className={`evt-media-zoom-layer-stage${isPanning ? " evt-media-zoom-layer-stage--dragging" : ""}`}
          onPointerDown={handlePanStart}
          onPointerMove={handlePanMove}
          onPointerUp={handlePanEnd}
          onPointerCancel={handlePanEnd}
        >
          <img
            className="evt-media-img evt-media-img--zoom-layer"
            src={mediaUrl}
            alt={titulo || "Multimedia"}
            draggable={false}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: "center center",
            }}
          />
        </div>
      </div>
    );
  };

  const renderVideoToolbar = () => {
    if (!isVideo || !videoMeta?.hasMedia) return null;
    const showDownload = Boolean(videoMeta?.downloadUrl);

    return (
      <div
        className="evt-media-toolbar evt-media-toolbar--video"
        role="toolbar"
        aria-label="Información del video"
      >
        <div className="evt-media-toolbar-desc">
          {videoMeta?.contenidoHtml ? (
            <div
              className="evt-media-video-desc evt-rich-html"
              dangerouslySetInnerHTML={{ __html: videoMeta.contenidoHtml }}
            />
          ) : titulo ? (
            <div className="evt-media-video-desc">{titulo}</div>
          ) : (
            <div className="evt-media-video-desc" aria-hidden="true">
              &nbsp;
            </div>
          )}
        </div>

        {showDownload ? (
          <button
            type="button"
            className="evt-media-download"
            onClick={downloadVideo}
            disabled={downloadBusy}
            aria-label="Descargar video"
            data-sgc-tooltip="Descargar video"
          >
            <i className="bi bi-download" aria-hidden />
            <span>Descargar</span>
          </button>
        ) : null}
      </div>
    );
  };

  const renderToolbar = () =>
    canZoomDownload ? (
      <div
        className="evt-media-toolbar"
        role="toolbar"
        aria-label="Controles de imagen"
      >
        <div className="evt-media-zoom" aria-label="Zoom">
          <button
            type="button"
            className="evt-media-btn"
            onClick={() =>
              setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))
            }
            aria-label="Alejar"
            data-sgc-tooltip="Alejar"
          >
            −
          </button>
          <span className="evt-media-zoom-value" aria-live="polite">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="evt-media-btn"
            onClick={() =>
              setZoom((z) => Math.min(2.5, Math.round((z + 0.1) * 10) / 10))
            }
            aria-label="Acercar"
            data-sgc-tooltip="Acercar"
          >
            +
          </button>
          <button
            type="button"
            className="evt-media-btn evt-media-btn--ghost"
            onClick={() => setZoom(DEFAULT_ZOOM)}
            aria-label="Restablecer zoom"
            data-sgc-tooltip="Restablecer zoom"
          >
            Restablecer
          </button>
        </div>

        <button
          type="button"
          className="evt-media-download"
          onClick={downloadCurrent}
          disabled={downloadBusy}
          aria-label="Descargar imagen"
          data-sgc-tooltip="Descargar imagen"
        >
          <i className="bi bi-download" aria-hidden />
          <span>Descargar</span>
        </button>
      </div>
    ) : null;

  const showSideNav = list.length > 1;

  return (
    <div
      className={`evt-media-carousel${zoomExpanded ? " evt-media-carousel--zoomed" : ""}`}
    >
      {renderZoomLayer()}
      {canZoomDownload || isVideo ? (
        <div
          className={`evt-media-image${isVideo ? " evt-media-image--video" : ""}`}
        >
          {isVideo ? renderVideoToolbar() : renderToolbar()}
          <div className="evt-media-body">
            <div className="evt-media-wrapper evt-media-wrapper--inset">
              {showSideNav ? (
                <button
                  type="button"
                  className="evt-nav-btn left"
                  onClick={anterior}
                  aria-label="Anterior"
                >
                  <i className="bi bi-chevron-left" aria-hidden />
                </button>
              ) : null}
              <div className="evt-media-imgwrap">{renderMedia()}</div>
              {showSideNav ? (
                <button
                  type="button"
                  className="evt-nav-btn right"
                  onClick={siguiente}
                  aria-label="Siguiente"
                >
                  <i className="bi bi-chevron-right" aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {titulo || referencia ? (
        <div className="evt-media-caption">
          {titulo ? (
            <div className="evt-media-caption-title" title={titulo}>
              {titulo}
            </div>
          ) : null}
          {referencia ? (
            <div className="evt-media-caption-ref">{referencia}</div>
          ) : null}
        </div>
      ) : null}

      {list.length > 1 ? (
        <div className="evt-indicadores" aria-label="Indicadores">
          {list.map((_, i) => (
            <span
              key={i}
              className={`evt-dot ${i === indexActual ? "active" : ""}`}
              onClick={() => setIndexActual(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setIndexActual(i);
              }}
              aria-label={`Ver elemento ${i + 1}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
